import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useAnularRetencion,
  useEnviarRetencionTfhka,
  useRetencionesPanel,
} from '../api/queries-finanzas';
import { descargarArchivo } from '../api/client';
import { downloadCsv } from '../lib/csv';
import { formatDate, formatMoney, todayIso } from '../lib/format';
import { useToast } from '../ui/ToastProvider';
import { MotivoModal } from '../components/MotivoModal';
import type { Retencion } from '../api/types';

const COLUMNS = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'comprobante', label: 'Comprobante' },
  { key: 'origen', label: 'Origen' },
  { key: 'tercero', label: 'Tercero' },
  { key: 'rif', label: 'RIF' },
  { key: 'documento', label: 'Documento' },
  { key: 'islr_pct', label: '% ISLR' },
  { key: 'islr_ves', label: 'ISLR Bs.' },
  { key: 'iva_ves', label: 'IVA ret. Bs.' },
  { key: 'total_ves', label: 'Total Bs.' },
  { key: 'anulada', label: 'Anulada' },
];

function estadoTfhka(retencion: Retencion): { label: string; tone: string } {
  if (retencion.anulada) return { label: 'Anulada', tone: 'pill pill--warning' };
  if (retencion.origen === 'PROVEEDOR') return { label: 'Vigente', tone: 'pill pill--soft' };
  switch (retencion.tfhka_estado) {
    case 'APLICADA':
      return { label: 'En TFHKA', tone: 'pill pill--soft' };
    case 'PENDIENTE':
      return { label: 'Pendiente TFHKA', tone: 'pill pill--warning' };
    case 'ERROR':
      return { label: 'Error TFHKA', tone: 'pill pill--warning' };
    default:
      return { label: 'Sin TFHKA', tone: 'pill pill--soft' };
  }
}

export function RetencionesPage() {
  const { user } = useAuth();
  const { notify } = useToast();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [origen, setOrigen] = useState<'' | 'CLIENTE' | 'PROVEEDOR'>('');
  const [incluirAnuladas, setIncluirAnuladas] = useState(true);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [annulRetencion, setAnnulRetencion] = useState<Retencion | null>(null);

  const params = {
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
    ...(origen ? { origen } : {}),
    incluirAnuladas,
  };
  const { data, isLoading } = useRetencionesPanel(params);
  const anular = useAnularRetencion();
  const enviar = useEnviarRetencionTfhka();
  const rows = data?.data ?? [];
  const totales = data?.totales ?? {};
  const esAdmin = user?.rol === 'admin';

  const descargar = async (formato: 'pdf' | 'xlsx') => {
    const search = new URLSearchParams({ formato });
    if (desde) search.set('desde', desde);
    if (hasta) search.set('hasta', hasta);
    if (origen) search.set('origen', origen);
    search.set('incluirAnuladas', String(incluirAnuladas));
    setExporting(formato);
    try {
      const blob = await descargarArchivo(`/api/retenciones/export?${search.toString()}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retenciones-${desde || 'inicio'}-${hasta || todayIso()}.${formato}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo descargar el reporte.', 'error');
    } finally {
      setExporting(null);
    }
  };

  const exportarCsv = () => {
    downloadCsv(
      `retenciones-${desde || 'inicio'}-${hasta || todayIso()}.csv`,
      COLUMNS,
      rows as unknown as Array<Record<string, unknown>>,
    );
  };

  const confirmarAnulacion = async (motivo: string) => {
    if (!annulRetencion) return;
    const result = await anular.mutateAsync({
      origen: annulRetencion.origen,
      id: annulRetencion.id,
      motivo,
    });
    notify(
      `Retención anulada con asiento #${result.data.asientoNumero}. Nuevo saldo del documento: ${formatMoney(result.data.saldoDocumento, 'USD')}.`,
      'success',
    );
    if (result.data.aviso) notify(result.data.aviso, 'warning');
    setAnnulRetencion(null);
  };

  const enviarTfhka = async (retencion: Retencion) => {
    try {
      const result = await enviar.mutateAsync(retencion.id);
      const { estado, mensaje } = result.data;
      const tono = estado === 'APLICADA' ? 'success' : estado === 'ERROR' ? 'error' : 'warning';
      notify(
        `${retencion.comprobante ?? 'Retención'}: ${mensaje ?? estado}`,
        tono,
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo enviar a TFHKA.', 'error');
    }
  };

  return (
    <div className="dashboard retenciones-page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Fiscal</p>
          <h1 className="page-title">Retenciones ISLR / IVA</h1>
          <p className="page-sub">
            Retenciones que aplican los clientes y las que aplica GRAVENCA a sus proveedores.
            Descarga el listado o anula una retención con su reverso contable.
          </p>
        </div>
      </header>

      <div className="page-tabs-row">
        <div className="filter-controls">
          <input
            type="date"
            className="input input--date"
            value={desde}
            onChange={(event) => setDesde(event.target.value)}
            aria-label="Desde"
          />
          <input
            type="date"
            className="input input--date"
            value={hasta}
            onChange={(event) => setHasta(event.target.value)}
            aria-label="Hasta"
          />
          <select
            className="input"
            value={origen}
            onChange={(event) => setOrigen(event.target.value as typeof origen)}
            aria-label="Origen"
          >
            <option value="">Clientes y proveedores</option>
            <option value="CLIENTE">Solo clientes</option>
            <option value="PROVEEDOR">Solo proveedores</option>
          </select>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={incluirAnuladas}
              onChange={(event) => setIncluirAnuladas(event.target.checked)}
            />
            Incluir anuladas
          </label>
        </div>
      </div>

      <div className="summary-strip">
        <div className="summary-tile">
          <span>ISLR retenido</span>
          <strong>{formatMoney(Number(totales.islrVes ?? 0), 'VES')}</strong>
          <small>{Number(totales.documentos ?? 0)} retenciones vigentes</small>
        </div>
        <div className="summary-tile">
          <span>IVA retenido</span>
          <strong>{formatMoney(Number(totales.ivaVes ?? 0), 'VES')}</strong>
          <small>Solo proveedores</small>
        </div>
        <div className="summary-tile summary-tile--accent">
          <span>Anuladas</span>
          <strong>{Number(totales.anuladas ?? 0)}</strong>
          <small>Con reverso contable</small>
        </div>
      </div>

      <section className="card transactions-card">
        <div className="table-scroll">
          <table className="data-table data-table--compact retenciones-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Comprobante</th>
                <th>Origen</th>
                <th>Tercero / RIF</th>
                <th>Documento</th>
                <th className="align-right">% ISLR</th>
                <th className="align-right">ISLR Bs.</th>
                <th className="align-right">IVA ret. Bs.</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="table-empty">Cargando retenciones…</div>
                  </td>
                </tr>
              ) : null}
              {!isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="table-empty">Sin retenciones en el período.</div>
                  </td>
                </tr>
              ) : null}
              {rows.map((retencion) => {
                const estado = estadoTfhka(retencion);
                return (
                  <tr key={`${retencion.origen}-${retencion.id}`}>
                    <td>{formatDate(retencion.fecha)}</td>
                    <td>
                      <div className="doc-cell">
                        <strong>{retencion.comprobante ?? '—'}</strong>
                        <span>{retencion.anulada_at ? `Anulada ${retencion.anulada_at}` : retencion.tipo_documento}</span>
                      </div>
                    </td>
                    <td>{retencion.origen === 'CLIENTE' ? 'Cliente' : 'Proveedor'}</td>
                    <td>
                      <div className="doc-cell">
                        <strong>{retencion.tercero}</strong>
                        <span>{retencion.rif}</span>
                      </div>
                    </td>
                    <td>{retencion.documento}</td>
                    <td className="align-right">{retencion.islr_pct.toFixed(2)}%</td>
                    <td className="align-right">{formatMoney(retencion.islr_ves, 'VES')}</td>
                    <td className="align-right">
                      {retencion.iva_ves > 0 ? formatMoney(retencion.iva_ves, 'VES') : '—'}
                    </td>
                    <td>
                      <span className={estado.tone} title={retencion.tfhka_mensaje ?? undefined}>
                        {estado.label}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        {retencion.origen === 'CLIENTE' &&
                        !retencion.anulada &&
                        retencion.tfhka_estado !== 'APLICADA' ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={enviar.isPending}
                            onClick={() => void enviarTfhka(retencion)}
                            title={retencion.tfhka_mensaje ?? 'Aplicar la retención en TFHKA'}
                          >
                            Enviar TFHKA
                          </button>
                        ) : null}
                        {esAdmin && !retencion.anulada ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={anular.isPending}
                            onClick={() => setAnnulRetencion(retencion)}
                          >
                            Anular
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>
            {rows.length} retenciones · período {data?.desde ?? '—'} → {data?.hasta ?? '—'}
          </span>
          <div className="row-actions">
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => void descargar('pdf')}
              disabled={rows.length === 0 || exporting !== null}
            >
              {exporting === 'pdf' ? 'Generando…' : 'PDF'}
            </button>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => void descargar('xlsx')}
              disabled={rows.length === 0 || exporting !== null}
            >
              {exporting === 'xlsx' ? 'Generando…' : 'XLSX'}
            </button>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={exportarCsv}
              disabled={rows.length === 0}
            >
              CSV
            </button>
          </div>
        </footer>
      </section>

      <p className="page-footnote">
        Las retenciones de clientes se aplican en TFHKA sobre la factura cuando el comprobante
        ya existe. Anular una retención restaura el saldo del documento y genera el asiento de
        reverso; el comprobante del tercero debe corregirse con la imprenta o el cliente.
      </p>

      {annulRetencion ? (
        <MotivoModal
          title={`Anular retención ${annulRetencion.comprobante ?? ''}`.trim()}
          description={`${annulRetencion.origen === 'CLIENTE' ? 'Cliente' : 'Proveedor'}: ${annulRetencion.tercero}. Documento ${annulRetencion.documento}.`}
          onClose={() => setAnnulRetencion(null)}
          onConfirm={confirmarAnulacion}
          footnote="Se restituye el saldo del documento y se reversa la retención. Si ya estaba aplicada en TFHKA, corrige también el comprobante con la imprenta o el cliente."
        />
      ) : null}
    </div>
  );
}
